import {
    CheckCircle,
    EmojiEvents,
    FlightTakeoff,
    Groups,
    LocalOffer,
    Security,
    Speed,
    Star,
    Support,
    Timeline,
    TrendingUp,
    Verified
} from '@mui/icons-material';
import {
    alpha,
    Box,
    Container,
    Grid,
    lighten,
    styled,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { motion, useInView, Variants } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { getOrDefault } from '../../../../Helpers/Utils';
import { Feature, IconMap, Statistic, WhyChooseUsProps } from './types';

// Styled glassmorphic card
const GlassCard = styled(Box)(({ theme }) => ({
    position: 'relative',
    height: '100%',
    borderRadius: 24,
    padding: theme.spacing(4),
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 32px 0 rgba(0, 0, 0, 0.5)'
        : '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'linear-gradient(90deg, transparent, currentColor, transparent)',
        opacity: 0,
        transition: 'opacity 0.4s ease'
    },
    '&:hover': {
        transform: 'translateY(-12px) scale(1.02)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 20px 60px 0 rgba(0, 0, 0, 0.7)'
            : '0 20px 60px 0 rgba(0, 0, 0, 0.2)',
        background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(255, 255, 255, 0.85)',
        '&::before': {
            opacity: 1
        }
    }
}));

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number }> = ({ end, duration = 2 }) => {
    const [count, setCount] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true });

    useEffect(() => {
        if (!isInView) return;

        let startTime: number | null = null;
        const animate = (currentTime: number) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);

            setCount(Math.floor(progress * end));

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    }, [isInView, end, duration]);

    return <span ref={ref}>{count}</span>;
};

const WhyChooseUs: React.FC<WhyChooseUsProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const isDownMedium = useMediaQuery(theme.breakpoints.down('md'));

    // Extract spec properties with defaults
    const backgroundColor = getOrDefault(spec.backgroundColor, theme.palette.background.default);
    const titleColor = getOrDefault(spec.titleColor, theme.palette.text.primary);
    const subtitleColor = getOrDefault(spec.subtitleColor, theme.palette.text.secondary);
    const accentColor = getOrDefault(spec.accentColor, theme.palette.primary.main);

    const cardsTitleColor = getOrDefault(spec.cardTitleColor, theme.palette.text.primary);
    const cardsSubtitleColor = getOrDefault(spec.cardSubtitleColor, theme.palette.text.secondary);

    const title = getOrDefault(spec.title, 'لماذا تختار سفرة وحكاية؟');
    const subtitle = getOrDefault(spec.subtitle, 'نحن نقدم تجربة سفر لا تُنسى مع أفضل الخدمات والأسعار');

    // Default features
    const defaultFeatures: Feature[] = [];
    const features = getOrDefault(spec.features, defaultFeatures).slice(0, isDownMedium ? 3 : 6);

    // Default statistics
    const defaultStatistics: Statistic[] = [];
    const statistics = getOrDefault(spec.statistics, defaultStatistics);

    // Icon mapping
    const iconMap: IconMap = {
        experience: Timeline,
        price: LocalOffer,
        support: Support,
        rating: Star,
        security: Security,
        destinations: FlightTakeoff,
        speed: Speed,
        trending: TrendingUp,
        verified: Verified,
        award: EmojiEvents,
        groups: Groups,
        check: CheckCircle
    };

    // Animation variants
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants: Variants = {
        hidden: {
            opacity: 0.8,
            // y: 60,
            // scale: 0.95
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring" as const,
                stiffness: 80,
                damping: 15
            }
        }
    };

    const floatingAnimation: Variants = {
        animate: {
            y: [0, -5, 0],
            transition: {
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut" as const
            }
        }
    };

    // Feature card component
    const FeatureCard: React.FC<{ feature: Feature; cardIndex: number }> = ({ feature, cardIndex }) => {
        const IconComponent = iconMap[feature.icon] || CheckCircle;

        return (
            <motion.div
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
            >
                <GlassCard
                    sx={{
                        color: feature.color,
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: -100,
                            right: -100,
                            width: 200,
                            height: 200,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha(feature.color, 0.15)}, transparent)`,
                            pointerEvents: 'none'
                        }
                    }}
                >
                    {/* Floating icon container */}
                    <motion.div
                        variants={floatingAnimation}
                        animate="animate"
                        style={{
                            display: 'inline-block',
                            marginBottom: theme.spacing(3)
                        }}
                    >
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: theme.palette.mode === 'dark'
                                    ? `linear-gradient(135deg, ${lighten(feature.color, 0.9)}, ${alpha(feature.color, 0.9)})`
                                    : `linear-gradient(135deg, ${alpha(feature.color, 0.2)}, ${alpha(feature.color, 0.05)})`,
                                border: `2px solid ${alpha(feature.color, 0.3)}`,
                                position: 'relative',
                                transition: 'all 0.4s ease',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: -2,
                                    borderRadius: '20px',
                                    padding: '2px',
                                    background: `linear-gradient(135deg, ${feature.color}, transparent)`,
                                    WebkitMask: theme.palette.mode === 'dark'
                                        ? 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)'
                                        : 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude',
                                    opacity: 0,
                                    transition: 'opacity 0.4s ease'
                                },
                                '&:hover::before': {
                                    opacity: 1
                                }
                            }}
                        >
                            <IconComponent
                                sx={{
                                    fontSize: 40,
                                    color: feature.color,
                                    filter: `drop-shadow(0 4px 8px ${alpha(feature.color, 0.3)})`
                                }}
                            />
                        </Box>
                    </motion.div>

                    {/* Content */}
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            mb: 1.5,
                            color: cardsTitleColor,
                            fontSize: { xs: '1.25rem', md: '1.4rem' },
                            lineHeight: 1.3
                        }}
                    >
                        {feature.title}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: cardsSubtitleColor,
                            lineHeight: 1.8,
                            fontSize: { xs: '0.95rem', md: '1rem' },
                            opacity: 0.85
                        }}
                    >
                        {feature.description}
                    </Typography>

                    {/* Decorative corner element */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -10,
                            right: -10,
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha(feature.color, 0.1)}, transparent)`,
                            pointerEvents: 'none'
                        }}
                    />
                </GlassCard>
            </motion.div>
        );
    };

    return (
        <Box
            id={spec.id}
            component="section"
            sx={{
                py: { xs: 8, md: 12 },
                backgroundColor,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Animated background gradient orbs */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '10%',
                    left: '5%',
                    width: 400,
                    height: 400,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(accentColor, 0.1)}, transparent)`,
                    filter: 'blur(60px)',
                    animation: 'float 20s ease-in-out infinite',
                    '@keyframes float': {
                        '0%, 100%': { transform: 'translate(0, 0)' },
                        '50%': { transform: 'translate(50px, 50px)' }
                    }
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '10%',
                    right: '5%',
                    width: 500,
                    height: 500,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(accentColor, 0.08)}, transparent)`,
                    filter: 'blur(80px)',
                    animation: 'float-reverse 25s ease-in-out infinite',
                    '@keyframes float-reverse': {
                        '0%, 100%': { transform: 'translate(0, 0)' },
                        '50%': { transform: 'translate(-50px, -50px)' }
                    }
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: "easeOut" as const }}
                >
                    <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
                        <motion.div
                            initial={{ scale: 0.9 }}
                            whileInView={{ scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 900,
                                    mb: 3,
                                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                                    background: `linear-gradient(135deg, ${titleColor} 0%, ${accentColor} 100%)`,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    lineHeight: 1.2,
                                    letterSpacing: '-0.02em',
                                    position: 'relative',
                                    display: 'inline-block',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: -10,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 100,
                                        height: 4,
                                        borderRadius: 2,
                                        background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`
                                    }
                                }}
                            >
                                {title}
                            </Typography>
                        </motion.div>
                        <Typography
                            variant="h5"
                            sx={{
                                color: subtitleColor,
                                maxWidth: 700,
                                mx: 'auto',
                                lineHeight: 1.7,
                                fontSize: { xs: '1.1rem', md: '1.25rem' },
                                fontWeight: 400,
                                mt: 4
                            }}
                        >
                            {subtitle}
                        </Typography>
                    </Box>
                </motion.div>

                {/* Features Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                >
                    <Grid container spacing={{ xs: 3, md: 4, lg: 5 }}>
                        {features.map((feature: Feature, idx: number) => (
                            <Grid item xs={12} sm={6} lg={4} key={idx}>
                                <FeatureCard feature={feature} cardIndex={idx} />
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>

                {/* Statistics Section */}
                {statistics.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                    >
                        <Box
                            sx={{
                                mt: { xs: 8, md: 12 },
                                p: { xs: 4, md: 6 },
                                borderRadius: 4,
                                background: theme.palette.mode === 'dark'
                                    ? `linear-gradient(135deg, ${alpha(accentColor, 0.15)}, ${alpha(accentColor, 0.05)})`
                                    : `linear-gradient(135deg, ${alpha(accentColor, 0.05)}, ${alpha(accentColor, 0.15)})`,
                                backdropFilter: 'blur(10px)',
                                border: `1px solid ${alpha(accentColor, 0.2)}`,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '100%',
                                    background: theme.palette.mode === 'dark'
                                        ? `linear-gradient(135deg, ${alpha('#fff', 0.03)}, transparent)`
                                        : `linear-gradient(135deg, ${alpha('#fff', 0.1)}, transparent)`,
                                    pointerEvents: 'none'
                                }
                            }}
                        >
                            <Grid container spacing={{ xs: 3, md: 4 }}>
                                {statistics.map((stat: Statistic, idx: number) => (
                                    <Grid item xs={6} md={3} key={idx}>
                                        <motion.div
                                            initial={{ scale: 0.8, opacity: 0 }}
                                            whileInView={{ scale: 1, opacity: 1 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: idx * 0.1, duration: 0.5 }}
                                            whileHover={{ scale: 1.1 }}
                                        >
                                            <Box
                                                sx={{
                                                    textAlign: 'center',
                                                    position: 'relative',
                                                    zIndex: 1
                                                }}
                                            >
                                                <Typography
                                                    variant="h2"
                                                    sx={{
                                                        fontWeight: 900,
                                                        mb: 1,
                                                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                                                        background: theme.palette.mode === 'dark'
                                                            ? `linear-gradient(135deg, #fff, ${alpha(theme.palette.primary.light, 0.9)})`
                                                            : `linear-gradient(135deg, ${accentColor}, ${alpha(accentColor, 0.7)})`,
                                                        backgroundClip: 'text',
                                                        WebkitBackgroundClip: 'text',
                                                        WebkitTextFillColor: 'transparent',
                                                        lineHeight: 1,
                                                        letterSpacing: '-0.02em',
                                                        filter: theme.palette.mode === 'dark'
                                                            ? `drop-shadow(0 2px 8px ${alpha(theme.palette.primary.light, 0.4)})`
                                                            : `drop-shadow(0 2px 8px ${alpha(accentColor, 0.3)})`
                                                    }}
                                                >
                                                    {/^\d+$/.test(stat.value) ? (
                                                        <>
                                                            <AnimatedCounter end={parseInt(stat.value)} />
                                                            {stat.value.includes('+') && '+'}
                                                        </>
                                                    ) : (
                                                        stat.value
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: subtitleColor,
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.9rem', md: '1rem' },
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em'
                                                    }}
                                                >
                                                    {stat.label}
                                                </Typography>
                                            </Box>
                                        </motion.div>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </motion.div>
                )}
            </Container>
        </Box>
    );
};

export default WhyChooseUs;
